import { RetryableOptions, retryable } from '@eng-portal/czi-extensions-common';
import { SimpleStorage } from '@okta/okta-auth-js';
import { get } from 'lodash';

type GetOptions = {
  fetchFn: () => Promise<any>;
  ttlSeconds: number;
  retryOptions?: RetryableOptions;
  validateFn?: (item: any) => boolean;
  storage?: Storage;
};

type CachedEntry = {
  item: any;
  expiration: Date;
};

const managedKeyPrefix = '/LocalStorageCache/';

function managedKey(key: string): string {
  return `${managedKeyPrefix}${key}`;
}

export class LocalStorageCache implements SimpleStorage {
  private readonly storage: Storage;

  constructor(storage: Storage = window.localStorage) {
    this.storage = storage;
  }

  getItem(key: string): any {
    return this.storage.getItem(managedKey(key));
  }

  setItem(key: string, value: any): void {
    this.storage.setItem(managedKey(key), value);
  }

  removeItem(key: string): void {
    this.storage.removeItem(managedKey(key));
  }

  static async get<T = any>(key: string, opts: GetOptions): Promise<T> {
    const retryOptions = get(opts, 'retryOptions', {});
    const validateFn = get(opts, 'validateFn', () => true);
    const storage = get(opts, 'storage', window.localStorage);
    const cacheKey = managedKey(key);

    const cachedItem = storage.getItem(cacheKey);
    if (cachedItem) {
      const cachedItemJson: CachedEntry = JSON.parse(cachedItem);
      const expiration = get(cachedItemJson, 'expiration');
      const item = get(cachedItemJson, 'item');
      const validItem = item && validateFn(item);
      if (
        validItem &&
        expiration &&
        new Date(expiration) > new Date(Date.now())
      ) {
        return item;
      }
    }

    const item = await retryable(opts.fetchFn, retryOptions);
    if (!item) {
      throw new Error(`Failed to retrieve value for key: ${key}`);
    }

    const expiration = new Date(Date.now() + opts.ttlSeconds * 1000);
    const entry: CachedEntry = { item, expiration };
    const itemJson = JSON.stringify(entry);

    storage.setItem(cacheKey, itemJson);
    return item;
  }

  static clear(opts?: { storage: Storage }): void {
    const storage = get(opts, 'storage', window.localStorage);
    Object.keys(storage).forEach(key => {
      if (key.startsWith(managedKeyPrefix)) {
        storage.removeItem(key);
      }
    });
  }
}
